import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  Fade,
  Divider,
  alpha,
  useTheme
} from '@mui/material';
import { renderIcon } from '../../../../helpers/renderDynamicIcons';
import { useTranslation } from 'react-i18next';
import { KeyboardArrowDown, ArrowForward } from '@mui/icons-material';

const MegaMenuTemplate = ({ 
  items, 
  onItemClick, 
  flatNavigationItems 
}) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ left: 0, width: 0 });
  const [t] = useTranslation();
  const theme = useTheme();
  const buttonRefs = useRef({});

  const getChildren = (item) => {
    if (item.children && Array.isArray(item.children)) {
      return item.children;
    }
    if (item.hasChildren && flatNavigationItems.length > 0) {
      return flatNavigationItems.filter(child => child.parentId === item.id);
    }
    return [];
  };

  const hasChildren = (item) => {
    const children = getChildren(item);
    return children.length > 0;
  };

  const handleMouseEnter = (item, event) => {
    if (hasChildren(item)) {
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      setMenuPosition({
        left: rect.left,
        width: rect.width
      });
      setActiveMenu(item.id);
    }
  };

  const handleMouseLeave = () => {
    // Small delay to allow moving to submenu
    setTimeout(() => {
      setActiveMenu(null);
    }, 100);
  };

  const handleItemClickInternal = (item) => {
    if (!hasChildren(item)) {
      setActiveMenu(null);
      onItemClick(item);
    }
  };

  const renderMegaMenuContent = (item) => {
    const children = getChildren(item);
    
    // Group children by category if they have a category property
    const groupedChildren = children.reduce((acc, child) => {
      const category = child.category || 'General';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(child);
      return acc;
    }, {});

    return (
      <Box
        onMouseEnter={() => setActiveMenu(item.id)}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 1300,
          pointerEvents: activeMenu === item.id ? 'auto' : 'none',
        }}
      >
        <Fade in={activeMenu === item.id} timeout={200}>
          <Paper
            elevation={8}
            sx={{
              mt: 2,
              borderRadius: 3,
              overflow: 'hidden',
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.3)}`,
            }}
          >
            <Box sx={{ p: 4 }}>
              {/* Header with gradient */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                    }}
                  >
                    {renderIcon(item.icon, { 
                      sx: { fontSize: 28, color: 'white' } 
                    })}
                  </Box>
                  <Typography 
                    variant="h5" 
                    fontWeight={700}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${alpha(theme.palette.text.primary, 0.7)} 100%)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {t(item.name)}
                  </Typography>
                </Box>
                <Divider sx={{ 
                  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, transparent 100%)`,
                  height: 2,
                  border: 'none'
                }} />
              </Box>

              {/* Grid Layout for Categories */}
              <Grid container spacing={4}>
                {Object.entries(groupedChildren).map(([category, categoryItems]) => (
                  <Grid item xs={12} sm={6} md={4} key={category}>
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontWeight: 700,
                          letterSpacing: 1.5,
                          mb: 2,
                          display: 'block'
                        }}
                      >
                        {category}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {categoryItems.map((child) => (
                          <Button
                            key={child.id}
                            onClick={() => handleItemClickInternal(child)}
                            fullWidth
                            sx={{
                              justifyContent: 'flex-start',
                              textAlign: 'left',
                              py: 1.5,
                              px: 2,
                              borderRadius: 2,
                              textTransform: 'none',
                              color: theme.palette.text.primary,
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              position: 'relative',
                              overflow: 'hidden',
                              '&:before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
                                transform: 'translateX(-100%)',
                                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              },
                              '&:hover': {
                                backgroundColor: 'transparent',
                                transform: 'translateX(4px)',
                                '&:before': {
                                  transform: 'translateX(0)',
                                },
                                '& .arrow-icon': {
                                  opacity: 1,
                                  transform: 'translateX(0)',
                                },
                              },
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1.5,
                              position: 'relative',
                              zIndex: 1,
                              width: '100%'
                            }}>
                              <Box
                                sx={{
                                  minWidth: 32,
                                  height: 32,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  transition: 'all 0.3s ease',
                                }}
                              >
                                {renderIcon(child.icon, { 
                                  sx: { 
                                    fontSize: 18,
                                    color: theme.palette.primary.main
                                  } 
                                })}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography 
                                  variant="body1" 
                                  fontWeight={500}
                                  sx={{ lineHeight: 1.2 }}
                                >
                                  {t(child.name)}
                                </Typography>
                                {child.description && (
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: theme.palette.text.secondary,
                                      display: 'block',
                                      mt: 0.5
                                    }}
                                  >
                                    {t(child.description)}
                                  </Typography>
                                )}
                              </Box>
                              <ArrowForward 
                                className="arrow-icon"
                                sx={{ 
                                  fontSize: 16,
                                  opacity: 0,
                                  transform: 'translateX(-8px)',
                                  transition: 'all 0.3s ease',
                                  color: theme.palette.primary.main
                                }}
                              />
                            </Box>
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </Fade>
      </Box>
    );
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Top Level Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {items?.filter(item => !item.parentId).map((item) => {
          const itemHasChildren = hasChildren(item);
          
          return (
            <Button
              key={item.id}
              ref={el => buttonRefs.current[item.id] = el}
              startIcon={renderIcon(item.icon)}
              endIcon={itemHasChildren ? <KeyboardArrowDown /> : null}
              onMouseEnter={(e) => handleMouseEnter(item, e)}
              onClick={() => handleItemClickInternal(item)}
              color="inherit"
              sx={{
                textTransform: 'none',
                px: 2,
                py: 1,
                borderRadius: 2,
                fontWeight: 500,
                position: 'relative',
                transition: 'all 0.3s ease',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%) scaleX(0)',
                  width: '80%',
                  height: 2,
                  backgroundColor: "#0b1633ff",//theme.palette.primary.main,
                  transition: 'transform 0.3s ease',
                },
                '&:hover': {
                  backgroundColor: alpha("#330b1aff", 0.80),
                  '&:before': {
                    transform: 'translateX(-50%) scaleX(1)',
                  },
                },
                ...(activeMenu === item.id && {
                  backgroundColor: alpha("#330b1aff", 0.32),
                  color: "white",
                  '&:before': {
                    transform: 'translateX(-50%) scaleX(1)',
                  },
                }),
              }}
            >
              {t(item.name)}
            </Button>
          );
        })}
      </Box>

      {/* Mega Menu Panels */}
      {items?.filter(item => !item.parentId).map((item) => (
        hasChildren(item) && renderMegaMenuContent(item)
      ))}
    </Box>
  );
};

export default MegaMenuTemplate;
